import React, { useEffect, useMemo, useState } from 'react';
import api from '@/lib/api';
import { toDatetimeLocalBrt, withTz, parseSafeDate } from '@/lib/dateBrt';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Clock, Pencil, Trash2, Plus, Save, X } from 'lucide-react';

const GREEN = '#0F6E56';
const EVENT_LABELS = {
  clock_in: 'Entrada',
  lunch_out: 'Saída almoço',
  lunch_in: 'Retorno almoço',
  clock_out: 'Saída',
};

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatTime(iso) {
  if (!iso) return '—';
  // Ensure the string carries a timezone marker; assume UTC when missing.
  return new Date(withTz(iso)).toLocaleTimeString('pt-BR', {
    timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit',
  });
}

function formatDuration(minutes) {
  if (!minutes && minutes !== 0) return '—';
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h && m) return `${h}h ${m}min`;
  if (h) return `${h}h`;
  return `${m}min`;
}

function formatDateShort(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
}

function dayOfWeek(iso) {
  const d = new Date(`${iso}T12:00:00-03:00`);
  return d.toLocaleDateString('pt-BR', { weekday: 'short', timeZone: 'America/Sao_Paulo' });
}

/**
 * Time Clock content — used as a tab inside UserManagement.
 * Props: user (current logged user)
 */
export default function TimeClockTab({ user }) {
  const isAdmin = (user?.role || '').toUpperCase() === 'ADMIN';

  const [month, setMonth] = useState(currentMonth());
  const [userFilter, setUserFilter] = useState('__all__');
  const [users, setUsers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  // Edit dialog state (admin)
  const [editEvent, setEditEvent] = useState(null);
  const [editForm, setEditForm] = useState({ event_type: '', datetime_local: '', notes: '' });
  const [saving, setSaving] = useState(false);

  // Manual entry dialog state (admin)
  const [manualOpen, setManualOpen] = useState(false);
  const [manualForm, setManualForm] = useState({
    user_id: '', event_type: 'clock_in', datetime_local: '', notes: '',
  });

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const { data } = await api.get('/users');
        setUsers((data || []).filter((u) =>
          ['ADMIN', 'CONSULTANT'].includes((u.role || '').toUpperCase()) && u.active,
        ));
      } catch {
        // ignore
      }
    })();
  }, [isAdmin]);

  const loadSummary = async () => {
    setLoading(true);
    const params = { month };
    if (isAdmin && userFilter && userFilter !== '__all__') params.user_id = userFilter;
    try {
      const { data } = await api.get('/time-clock/summary', { params });
      setSummary(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar histórico');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, userFilter]);

  const totals = useMemo(() => {
    if (!summary?.users) return { minutes: 0, days: 0, users: 0 };
    return {
      minutes: summary.users.reduce((s, u) => s + (u.total_minutes || 0), 0),
      days: summary.users.reduce((s, u) => s + (u.days_worked || 0), 0),
      users: summary.users.length,
    };
  }, [summary]);

  const openEdit = (userId, day, eventType) => {
    if (!isAdmin) return;
    const found = day.events.find((e) => e.event_type === eventType);
    if (!found) {
      // Create new via manual
      setManualForm({
        user_id: userId,
        event_type: eventType,
        datetime_local: `${day.date_key}T09:00`,
        notes: '',
      });
      setManualOpen(true);
      return;
    }
    // Convert stored UTC ISO to a BRT-local input value (YYYY-MM-DDTHH:MM)
    const brtDateTime = toDatetimeLocalBrt(parseSafeDate(found.timestamp));
    setEditEvent(found);
    setEditForm({
      event_type: found.event_type,
      datetime_local: brtDateTime,
      notes: found.notes || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editEvent) return;
    if (!editForm.datetime_local) return toast.error('Informe a data/hora.');
    setSaving(true);
    try {
      await api.put(`/time-clock/${editEvent.event_id}`, {
        event_type: editForm.event_type,
        timestamp: editForm.datetime_local,
        notes: (editForm.notes || '').trim() || null,
      });
      toast.success('Registro atualizado');
      setEditEvent(null);
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao salvar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!editEvent) return;
    if (!window.confirm('Excluir este registro de ponto?')) return;
    try {
      await api.delete(`/time-clock/${editEvent.event_id}`);
      toast.success('Registro excluído');
      setEditEvent(null);
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao excluir');
    }
  };

  const handleCreateManual = async () => {
    if (!manualForm.user_id) return toast.error('Selecione o usuário.');
    if (!manualForm.datetime_local) return toast.error('Informe a data/hora.');
    setSaving(true);
    try {
      await api.post('/time-clock/manual', {
        user_id: manualForm.user_id,
        event_type: manualForm.event_type,
        timestamp: manualForm.datetime_local,
        notes: (manualForm.notes || '').trim() || null,
      });
      toast.success('Ponto criado');
      setManualOpen(false);
      setManualForm({ user_id: '', event_type: 'clock_in', datetime_local: '', notes: '' });
      loadSummary();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao criar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="time-clock-tab-content">
      {/* Filters */}
      <Card className="p-4 border border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <Label htmlFor="tc-month">Mês</Label>
            <Input
              id="tc-month"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              data-testid="tc-month-filter"
            />
          </div>
          {isAdmin && (
            <div>
              <Label>Usuário</Label>
              <Select value={userFilter} onValueChange={setUserFilter}>
                <SelectTrigger data-testid="tc-user-filter">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.full_name || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {isAdmin && (
            <div className="flex justify-end">
              <Button
                onClick={() => {
                  setManualForm({
                    user_id: '',
                    event_type: 'clock_in',
                    datetime_local: '',
                    notes: '',
                  });
                  setManualOpen(true);
                }}
                style={{ backgroundColor: GREEN }}
                className="text-white hover:opacity-90"
                data-testid="tc-manual-add"
              >
                <Plus className="w-4 h-4 mr-1" /> Lançar ponto manual
              </Button>
            </div>
          )}
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4 border border-gray-200">
          <p className="text-xs text-gray-500">Horas trabalhadas no mês</p>
          <p className="text-2xl font-semibold" style={{ color: GREEN }}>
            {formatDuration(totals.minutes)}
          </p>
        </Card>
        <Card className="p-4 border border-gray-200">
          <p className="text-xs text-gray-500">Dias com jornada completa</p>
          <p className="text-2xl font-semibold text-gray-900">{totals.days}</p>
        </Card>
        <Card className="p-4 border border-gray-200">
          <p className="text-xs text-gray-500">
            {isAdmin ? 'Consultores com registros' : 'Meses considerados'}
          </p>
          <p className="text-2xl font-semibold text-gray-900">{totals.users}</p>
        </Card>
      </div>

      {/* Per user tables */}
      {loading ? (
        <Card className="p-6 text-center text-sm text-gray-500">Carregando...</Card>
      ) : (!summary?.users || summary.users.length === 0) ? (
        <Card className="p-6 text-center text-sm text-gray-500 italic">
          Nenhum registro de ponto neste mês.
        </Card>
      ) : (
        summary.users.map((u) => (
          <Card key={u.user_id} className="p-4 border border-gray-200"
            data-testid={`tc-user-block-${u.user_id}`}>
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
              <div>
                <p className="text-sm text-gray-500">Consultor</p>
                <h3 className="text-lg font-semibold text-gray-900">{u.user_name}</h3>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Total no mês</p>
                <p className="text-xl font-semibold" style={{ color: GREEN }}>
                  {formatDuration(u.total_minutes)}
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-gray-500 uppercase">
                    <th className="py-2">Dia</th>
                    <th className="py-2">Entrada</th>
                    <th className="py-2">Saída almoço</th>
                    <th className="py-2">Retorno almoço</th>
                    <th className="py-2">Saída</th>
                    <th className="py-2 text-right">Trabalhado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {u.days.map((day) => (
                    <tr key={`${u.user_id}-${day.date_key}`}
                      data-testid={`tc-day-row-${u.user_id}-${day.date_key}`}>
                      <td className="py-2 whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {formatDateShort(day.date_key)}
                        </div>
                        <div className="text-[10px] text-gray-500 capitalize">
                          {dayOfWeek(day.date_key)}
                        </div>
                      </td>
                      {['clock_in', 'lunch_out', 'lunch_in', 'clock_out'].map((t) => (
                        <td key={t} className="py-2">
                          <button
                            type="button"
                            disabled={!isAdmin}
                            onClick={() => openEdit(u.user_id, day, t)}
                            className={`text-sm ${isAdmin ? 'hover:text-emerald-700 cursor-pointer' : 'cursor-default'} ${day[t] ? 'text-gray-900' : 'text-gray-400 italic'}`}
                            title={isAdmin ? 'Clique para editar' : ''}
                            data-testid={`tc-cell-${u.user_id}-${day.date_key}-${t}`}
                          >
                            {formatTime(day[t]) || '—'}
                          </button>
                        </td>
                      ))}
                      <td className="py-2 text-right font-medium" style={{ color: GREEN }}>
                        {formatDuration(day.worked_minutes)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ))
      )}

      {/* Edit dialog (admin) */}
      <Dialog open={!!editEvent} onOpenChange={(o) => !o && setEditEvent(null)}>
        <DialogContent className="max-w-md" data-testid="tc-edit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-4 h-4" style={{ color: GREEN }} />
              Editar registro de ponto
            </DialogTitle>
            <DialogDescription>
              As alterações ficam auditadas com seu usuário.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Tipo de evento</Label>
              <Select
                value={editForm.event_type}
                onValueChange={(v) => setEditForm({ ...editForm, event_type: v })}
              >
                <SelectTrigger data-testid="tc-edit-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(EVENT_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="tc-edit-dt">Data e hora (BRT)</Label>
              <Input
                id="tc-edit-dt"
                type="datetime-local"
                value={editForm.datetime_local}
                onChange={(e) => setEditForm({ ...editForm, datetime_local: e.target.value })}
                data-testid="tc-edit-datetime"
              />
            </div>
            <div>
              <Label htmlFor="tc-edit-notes">Observação</Label>
              <Input
                id="tc-edit-notes"
                value={editForm.notes}
                onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                maxLength={300}
                data-testid="tc-edit-notes"
              />
            </div>
            <div className="flex items-center justify-between pt-2">
              <Button
                variant="outline"
                className="text-red-600 border-red-200 hover:bg-red-50"
                onClick={handleDelete}
                data-testid="tc-edit-delete"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Excluir
              </Button>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setEditEvent(null)}>
                  <X className="w-4 h-4 mr-1" /> Cancelar
                </Button>
                <Button
                  onClick={handleSaveEdit}
                  disabled={saving}
                  style={{ backgroundColor: GREEN }}
                  className="text-white hover:opacity-90"
                  data-testid="tc-edit-save"
                >
                  <Save className="w-4 h-4 mr-1" /> Salvar
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manual add dialog (admin) */}
      <Dialog open={manualOpen} onOpenChange={setManualOpen}>
        <DialogContent className="max-w-md" data-testid="tc-manual-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-4 h-4" style={{ color: GREEN }} /> Lançar ponto manual
            </DialogTitle>
            <DialogDescription>
              Use para corrigir esquecimentos ou registrar retroativamente.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Usuário</Label>
              <Select
                value={manualForm.user_id}
                onValueChange={(v) => setManualForm({ ...manualForm, user_id: v })}
              >
                <SelectTrigger data-testid="tc-manual-user">
                  <SelectValue placeholder="Selecione..." />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.user_id} value={u.user_id}>
                      {u.full_name || u.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Tipo</Label>
              <Select
                value={manualForm.event_type}
                onValueChange={(v) => setManualForm({ ...manualForm, event_type: v })}
              >
                <SelectTrigger data-testid="tc-manual-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(EVENT_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Data e hora (BRT)</Label>
              <Input
                type="datetime-local"
                value={manualForm.datetime_local}
                onChange={(e) => setManualForm({ ...manualForm, datetime_local: e.target.value })}
                data-testid="tc-manual-datetime"
              />
            </div>
            <div>
              <Label>Observação</Label>
              <Input
                value={manualForm.notes}
                onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })}
                maxLength={300}
                data-testid="tc-manual-notes"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setManualOpen(false)}>Cancelar</Button>
              <Button
                onClick={handleCreateManual}
                disabled={saving}
                style={{ backgroundColor: GREEN }}
                className="text-white hover:opacity-90"
                data-testid="tc-manual-save"
              >
                {saving ? 'Salvando...' : 'Registrar'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
